import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle, ArrowLeft, FileText, Clock, Shield } from 'lucide-react';
import ConsultationDialog from '../components/ConsultationDialog';

const ServicePage = () => {
  const { serviceSlug } = useParams();
  const [isConsultationOpen, setIsConsultationOpen] = useState(false);
  const [service, setService] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const servicesData = {
    'corporate-business-contracts': {
      title: 'Corporate & Business Contracts',
      description: 'Expert contract drafting, review, and negotiation services to protect your business interests and ensure favorable terms.',
      overview: 'Whether you\'re entering into a partnership agreement, vendor contract, or employment agreement, having well-drafted contracts is essential to protecting your business. Our attorneys have extensive experience in drafting, reviewing, and negotiating all types of business contracts.',
      benefits: [
        'Comprehensive contract review and analysis',
        'Custom contract drafting tailored to your needs',
        'Skilled negotiation to secure favorable terms',
        'Risk identification and mitigation strategies',
        'Ongoing contract management and compliance support',
      ],
      process: [
        'Initial consultation to understand your business needs',
        'Review of existing contracts or drafting new agreements',
        'Negotiation with counterparties to protect your interests',
        'Finalization and execution of agreements',
        'Ongoing support for contract amendments and disputes',
      ],
    },
    'corporate-business-formation': {
      title: 'Corporate & Business Formation',
      description: 'Comprehensive legal guidance for starting your business, from choosing the right entity structure to filing incorporation documents.',
      overview: 'Launching a new business requires careful planning and the right legal foundation. We help entrepreneurs and business owners navigate entity selection, incorporation, and compliance requirements to set your business up for success.',
      benefits: [
        'Expert guidance on entity selection (LLC, Corporation, Partnership)',
        'Preparation and filing of all formation documents',
        'Operating agreements and corporate bylaws',
        'EIN application and tax registration',
        'Compliance with state and federal regulations',
      ],
      process: [
        'Consultation to discuss your business goals',
        'Entity structure recommendation based on your needs',
        'Preparation of formation documents',
        'Filing with state authorities',
        'Ongoing compliance and governance support',
      ],
    },
    'business-transaction': {
      title: 'Business Transaction',
      description: 'Strategic legal counsel for business transactions including acquisitions, sales, financing, and partnership arrangements.',
      overview: 'Business transactions require meticulous attention to detail and strategic planning. Our team provides comprehensive legal support for all types of business transactions, ensuring smooth execution and favorable outcomes.',
      benefits: [
        'Due diligence and risk assessment',
        'Transaction structuring and planning',
        'Purchase and sale agreement negotiation',
        'Financing and capital raise support',
        'Post-closing integration assistance',
      ],
      process: [
        'Initial assessment of transaction goals',
        'Due diligence and document review',
        'Negotiation of terms and conditions',
        'Preparation of transaction documents',
        'Closing coordination and post-transaction support',
      ],
    },
    'mergers-acquisitions': {
      title: 'Mergers & Acquisitions',
      description: 'Expert M&A advisory services for buyers and sellers, from initial valuation through post-merger integration.',
      overview: 'Mergers and acquisitions are complex transactions that can transform your business. Our experienced M&A attorneys guide clients through every stage of the process, ensuring successful outcomes and value maximization.',
      benefits: [
        'Target identification and valuation analysis',
        'Comprehensive due diligence review',
        'Deal structure optimization for tax efficiency',
        'Skilled negotiation of purchase agreements',
        'Integration planning and execution support',
      ],
      process: [
        'Strategic planning and target identification',
        'Preliminary negotiations and letter of intent',
        'Extensive due diligence investigation',
        'Definitive agreement negotiation and drafting',
        'Regulatory approvals and closing',
      ],
    },
    'non-profit-law': {
      title: 'Non-Profit Law',
      description: 'Comprehensive legal services for non-profit organizations, including formation, tax-exempt status, and ongoing compliance.',
      overview: 'Non-profit organizations face unique legal challenges and regulatory requirements. We provide full-service legal support to help charitable organizations achieve their missions while maintaining compliance.',
      benefits: [
        'Non-profit entity formation and structuring',
        '501(c)(3) tax-exempt status application',
        'Board governance and bylaws development',
        'Grant compliance and reporting',
        'Ongoing regulatory compliance support',
      ],
      process: [
        'Consultation on mission and organizational structure',
        'Formation of non-profit entity',
        'IRS Form 1023 preparation and filing',
        'Development of governance documents',
        'Continuous compliance monitoring',
      ],
    },
    'government-contracting': {
      title: 'Government Contracting',
      description: 'Specialized legal counsel for businesses seeking government contracts, including bid preparation, compliance, and dispute resolution.',
      overview: 'Government contracting involves complex regulations and procedures. Our attorneys help businesses successfully navigate federal, state, and local procurement processes and maintain compliance with contracting requirements.',
      benefits: [
        'Contract bid preparation and review',
        'Compliance with FAR and agency-specific regulations',
        'Small business certification assistance',
        'Claims and dispute resolution',
        'Contract negotiation and modification',
      ],
      process: [
        'Assessment of contracting opportunities',
        'Preparation of proposals and bids',
        'Contract award negotiation',
        'Performance compliance monitoring',
        'Dispute resolution and claims support',
      ],
    },
    'business-disputes': {
      title: 'Business Disputes',
      description: 'Aggressive representation in business litigation, including contract disputes, partnership disagreements, and commercial conflicts.',
      overview: 'When business disputes arise, you need experienced litigators who will protect your interests and pursue favorable resolutions. Our trial attorneys handle all types of commercial disputes through negotiation, mediation, arbitration, and litigation.',
      benefits: [
        'Skilled negotiation and dispute resolution',
        'Experienced trial representation',
        'Alternative dispute resolution (mediation, arbitration)',
        'Contract breach and enforcement actions',
        'Business tort and fraud claims',
      ],
      process: [
        'Case evaluation and strategy development',
        'Pre-litigation negotiation attempts',
        'Filing of complaint or responsive pleadings',
        'Discovery and motion practice',
        'Trial or settlement resolution',
      ],
    },
    'anti-corruption-compliance': {
      title: 'Anti-Corruption Compliance',
      description: 'Comprehensive anti-corruption compliance programs, including FCPA guidance, internal investigations, and remediation.',
      overview: 'Anti-corruption laws like the Foreign Corrupt Practices Act impose significant obligations on businesses. We help companies develop robust compliance programs, conduct internal investigations, and respond to government inquiries.',
      benefits: [
        'FCPA and anti-bribery compliance programs',
        'Risk assessment and due diligence',
        'Internal investigation management',
        'Training and policy development',
        'Government enforcement defense',
      ],
      process: [
        'Compliance program assessment',
        'Risk identification and mitigation',
        'Policy and procedure development',
        'Employee training implementation',
        'Ongoing monitoring and updates',
      ],
    },
    'immigration': {
      title: 'Immigration',
      description: 'Full-service immigration law support for individuals and businesses, including visas, green cards, and citizenship applications.',
      overview: 'Navigating U.S. immigration law can be challenging. Our immigration attorneys provide comprehensive services for individuals seeking to live and work in the United States, as well as employers sponsoring foreign workers.',
      benefits: [
        'Work visas (H-1B, L-1, O-1, E-2)',
        'Employment-based green cards (EB-1, EB-2, EB-3)',
        'Family-based immigration petitions',
        'Citizenship and naturalization',
        'Deportation defense and removal proceedings',
      ],
      process: [
        'Eligibility assessment and strategy development',
        'Preparation of petitions and supporting documents',
        'Filing with USCIS or immigration court',
        'Response to requests for evidence',
        'Interview preparation and representation',
      ],
    },
    'trademark-registration': {
      title: 'Trademark Registration',
      description: 'Professional trademark search, registration, and enforcement services to protect your brand identity and business reputation.',
      overview: 'Your brand is one of your most valuable assets. We help businesses protect their trademarks through comprehensive clearance searches, strategic registration, and vigorous enforcement against infringement.',
      benefits: [
        'Comprehensive trademark clearance searches',
        'Federal trademark registration (USPTO)',
        'International trademark protection (Madrid Protocol)',
        'Trademark portfolio management',
        'Enforcement and infringement defense',
      ],
      process: [
        'Trademark availability search',
        'Application preparation and filing',
        'Response to Office Actions',
        'Registration issuance',
        'Ongoing monitoring and maintenance',
      ],
    },
    'copyright-registration': {
      title: 'Copyright Registration',
      description: 'Expert copyright registration and protection services for creative works, software, and digital content.',
      overview: 'Copyright protection is essential for creators, authors, and businesses that produce original works. We provide comprehensive copyright registration services and help enforce your rights against infringement.',
      benefits: [
        'Copyright registration with U.S. Copyright Office',
        'Work-for-hire and assignment agreements',
        'Licensing and distribution agreements',
        'DMCA takedown notices',
        'Copyright infringement litigation',
      ],
      process: [
        'Copyrightability assessment',
        'Preparation of registration application',
        'Filing with Copyright Office',
        'Certificate of registration',
        'Enforcement and licensing support',
      ],
    },
  };

  useEffect(() => {
    if (serviceSlug && servicesData[serviceSlug]) {
      setService(servicesData[serviceSlug]);
    }
  }, [serviceSlug]);

  // Update document title when service changes
  useEffect(() => {
    if (service) {
      document.title = `${service.title} - Zorvian Associates | Professional Legal Services`;
    }
  }, [service]);

  if (!service) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <h1 className="text-3xl font-bold text-[#0F172A] mb-4">Service Not Found</h1>
        <p className="text-slate-600 mb-8">The service you're looking for doesn't exist.</p>
        <Button asChild variant="outline">
          <Link to="/">Return to Home</Link>
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="service-page">
      
      {/* Enhanced Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0F172A] via-[#1e293b] to-[#0F172A] py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto">
            <Link
              to="/"
              className="inline-flex items-center text-white/80 hover:text-white mb-6 font-medium"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Link>
            <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 font-heading transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              {service.title}
            </h1>
            <p className={`text-lg text-slate-300 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>{service.description}</p>
          </div>
        </div>
      </section>
      
      {/* Intro Section */}
      <section className="py-12 bg-slate-50 border-b border-slate-200">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-lg text-slate-600 leading-relaxed">
              Our dedicated team at Zorvian Associates provides specialized legal services tailored to your business needs. 
              With years of expertise and a proven track record, we're committed to delivering exceptional results.
            </p>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="section-padding bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-[#0F172A] mb-6 font-heading">Overview</h2>
            <p className="text-lg text-slate-600 leading-relaxed">{service.overview}</p>
          </div>
        </div>
      </section>

      {/* Benefits & Process */}
      <section className="section-padding bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12">
            {/* Benefits */}
            <Card className="border-0 shadow-lg bg-white">
              <CardContent className="p-8">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="bg-blue-50 w-12 h-12 rounded-lg flex items-center justify-center">
                    <CheckCircle className="h-6 w-6 text-[#2563EB]" />
                  </div>
                  <h2 className="text-2xl font-bold text-[#0F172A] font-heading">What We Offer</h2>
                </div>
                <ul className="space-y-4">
                  {service.benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <CheckCircle className="h-5 w-5 text-[#2563EB] flex-shrink-0 mt-0.5" />
                      <span className="text-slate-600">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Process */}
            <Card className="border-0 shadow-lg bg-white">
              <CardContent className="p-8">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="bg-blue-50 w-12 h-12 rounded-lg flex items-center justify-center">
                    <FileText className="h-6 w-6 text-[#2563EB]" />
                  </div>
                  <h2 className="text-2xl font-bold text-[#0F172A] font-heading">Our Process</h2>
                </div>
                <ol className="space-y-4">
                  {service.process.map((step, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <div className="bg-blue-50 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-bold text-[#2563EB]">{index + 1}</span>
                      </div>
                      <span className="text-slate-600 mt-1">{step}</span>
                    </li>
                  ))}
                </ol>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-[#0F172A] mb-8 font-heading text-center">
              Why Choose Zorvian Associates?
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="bg-blue-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="h-8 w-8 text-[#2563EB]" />
                </div>
                <h3 className="text-lg font-bold text-[#0F172A] mb-2">Experienced Attorneys</h3>
                <p className="text-sm text-slate-600">Decades of combined experience in this practice area</p>
              </div>
              <div className="text-center">
                <div className="bg-blue-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="h-8 w-8 text-[#2563EB]" />
                </div>
                <h3 className="text-lg font-bold text-[#0F172A] mb-2">Responsive Service</h3>
                <p className="text-sm text-slate-600">We prioritize communication and keep you informed</p>
              </div>
              <div className="text-center">
                <div className="bg-blue-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-8 w-8 text-[#2563EB]" />
                </div>
                <h3 className="text-lg font-bold text-[#0F172A] mb-2">Proven Results</h3>
                <p className="text-sm text-slate-600">Track record of successful outcomes for our clients</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Client Benefits Section - NEW */}
      <section className="section-padding bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-[#0F172A] mb-4 font-heading">Benefits of Working With Us</h2>
              <p className="text-lg text-slate-600">
                Our comprehensive approach ensures your legal needs are handled with expertise and care
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="bg-white border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="bg-blue-50 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="h-6 w-6 text-[#2563EB]" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-[#0F172A] mb-2">Personalized Strategy</h3>
                      <p className="text-slate-600">We develop customized legal strategies tailored to your specific situation and goals.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="bg-blue-50 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="h-6 w-6 text-[#2563EB]" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-[#0F172A] mb-2">Clear Communication</h3>
                      <p className="text-slate-600">We explain complex legal matters in plain language and keep you updated at every step.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="bg-blue-50 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="h-6 w-6 text-[#2563EB]" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-[#0F172A] mb-2">Cost-Effective Solutions</h3>
                      <p className="text-slate-600">Transparent pricing and efficient processes ensure you get maximum value for your investment.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="bg-blue-50 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="h-6 w-6 text-[#2563EB]" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-[#0F172A] mb-2">Long-Term Partnership</h3>
                      <p className="text-slate-600">We build lasting relationships and serve as your trusted legal advisor for years to come.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Success Metrics Section - NEW */}
      <section className="section-padding bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-[#0F172A] mb-4 font-heading">Our Track Record</h2>
              <p className="text-lg text-slate-600">
                Proven results in {service.title.toLowerCase()} matters
              </p>
            </div>
            
            <div className="grid md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="text-4xl font-bold text-[#2563EB] mb-2">150+</div>
                <p className="text-slate-600">Successful Cases</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-[#2563EB] mb-2">98%</div>
                <p className="text-slate-600">Client Satisfaction</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-[#2563EB] mb-2">20+</div>
                <p className="text-slate-600">Years Experience</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-[#2563EB] mb-2">24hr</div>
                <p className="text-slate-600">Response Time</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section - NEW */}
      <section className="section-padding bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-[#0F172A] mb-4 font-heading">Frequently Asked Questions</h2>
              <p className="text-lg text-slate-600">
                Common questions about {service.title.toLowerCase()}
              </p>
            </div>
            
            <div className="space-y-6">
              <Card className="bg-white border-0 shadow-md">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-[#0F172A] mb-2">How long does the process typically take?</h3>
                  <p className="text-slate-600">
                    The timeline varies depending on the complexity of your case. During your initial consultation, 
                    we'll provide a realistic timeframe based on your specific circumstances.
                  </p>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-0 shadow-md">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-[#0F172A] mb-2">What are your fees?</h3>
                  <p className="text-slate-600">
                    We offer transparent pricing with several billing options. Contact us for a free consultation 
                    to discuss your needs and receive a detailed fee structure.
                  </p>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-0 shadow-md">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-[#0F172A] mb-2">Do I need to come to your office?</h3>
                  <p className="text-slate-600">
                    We offer both in-person and virtual consultations to accommodate your schedule and preferences. 
                    Many matters can be handled remotely for your convenience.
                  </p>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-0 shadow-md">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-[#0F172A] mb-2">What should I bring to the consultation?</h3>
                  <p className="text-slate-600">
                    Bring any relevant documents, contracts, or correspondence related to your matter. 
                    A timeline of events and list of questions will also be helpful.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services Section - NEW */}
      <section className="section-padding bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-[#0F172A] mb-4 font-heading">Related Legal Services</h2>
              <p className="text-lg text-slate-600">
                Additional services that may benefit your business
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              <Link to="/services/corporate-business-contracts" className="group">
                <Card className="bg-slate-50 border-0 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                  <CardContent className="p-6">
                    <FileText className="h-8 w-8 text-[#2563EB] mb-4" />
                    <h3 className="text-lg font-bold text-[#0F172A] mb-2 group-hover:text-[#2563EB] transition-colors">
                      Business Contracts
                    </h3>
                    <p className="text-sm text-slate-600">Expert contract drafting and negotiation</p>
                  </CardContent>
                </Card>
              </Link>
              
              <Link to="/services/corporate-business-formation" className="group">
                <Card className="bg-slate-50 border-0 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                  <CardContent className="p-6">
                    <Shield className="h-8 w-8 text-[#2563EB] mb-4" />
                    <h3 className="text-lg font-bold text-[#0F172A] mb-2 group-hover:text-[#2563EB] transition-colors">
                      Business Formation
                    </h3>
                    <p className="text-sm text-slate-600">Entity formation and structuring</p>
                  </CardContent>
                </Card>
              </Link>
              
              <Link to="/services/trademark-registration" className="group">
                <Card className="bg-slate-50 border-0 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                  <CardContent className="p-6">
                    <Shield className="h-8 w-8 text-[#2563EB] mb-4" />
                    <h3 className="text-lg font-bold text-[#0F172A] mb-2 group-hover:text-[#2563EB] transition-colors">
                      Trademark Protection
                    </h3>
                    <p className="text-sm text-slate-600">Brand protection and registration</p>
                  </CardContent>
                </Card>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Need Legal Assistance */}
      <section className="section-padding bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8 md:p-12 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-4 font-heading">Need Legal Assistance?</h2>
            <p className="text-lg text-slate-600 mb-8">
              Get in touch with our expert attorneys for a free consultation today. We're here to help you navigate your legal challenges with confidence.
            </p>
            <Button
              onClick={() => setIsConsultationOpen(true)}
              size="lg"
              className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-8 py-6 text-lg"
            >
              Contact Us
            </Button>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="section-padding bg-gradient-to-br from-[#0F172A] via-[#1e293b] to-[#0F172A] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl font-bold mb-6 font-heading">Ready to Get Started?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-slate-300">
            Schedule a free consultation to discuss your {service.title.toLowerCase()} needs with our experienced attorneys.
          </p>
          <Button
            onClick={() => setIsConsultationOpen(true)}
            size="lg"
            className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-8 py-6 text-lg shadow-xl shadow-blue-500/20"
            data-testid="service-cta-button"
          >
            Request Free Consultation
          </Button>
        </div>
      </section>

      <ConsultationDialog open={isConsultationOpen} onOpenChange={setIsConsultationOpen} />
    </div>
  );
};

export default ServicePage;
